import axios from '/src/utils/request'
import qs from 'qs'
import { getWebPid } from 'src/utils/product'
import { encryptPassword } from 'src/utils/passwordCrypto'
import {
  buildDebugIcloud10005Response,
  isDebugForceIcloud10005
} from 'src/utils/debugForceIcloud10005'

/**
 * 登录接口
 */

const getUserInfo = () => axios.get('/api/definition/user-info')

// 生成 traceID：时间戳 + 当前选中设备标识片段 + 随机串
// device_id 从 localStorage.DeviceUser 读取，与 redux Common.deviceUser 来源一致
// 目的：降低同毫秒撞值概率，同时方便从日志聚类到"同一台设备/同一用户场景"
const buildTraceID = () => {
  const ts = Date.now()

  let rawDeviceId = 'anon'
  try {
    const saved = JSON.parse(localStorage.getItem('DeviceUser'))
    rawDeviceId = saved?.device_id || saved?.username || 'anon'
  } catch (e) {
    // localStorage 不可用时兜底
  }
  const deviceSuffix = String(rawDeviceId).slice(-4)

  let randomHex
  try {
    const bytes = new Uint8Array(3)
    crypto.getRandomValues(bytes)
    randomHex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')
  } catch (e) {
    randomHex = Math.random().toString(36).slice(2, 8)
  }

  return `${ts}-${deviceSuffix}-${randomHex}`
}

// 设备登录
const authByDevice = options => axios.post('/api/dashboard/by-device', options)

// 设备登录验证码
const authVerifyByDevice = options => axios.post('/api/dashboard/verify/by-device', options)

// 下载数据（统一附带 web 产品 id）
const downloadWeb = async options => {
  const payload = { ...options, pid: getWebPid(), traceID: buildTraceID() }
  const startAt = Date.now()
  console.log('【下载web】发起请求', {
    时间: new Date(startAt).toLocaleString('zh-CN'),
    时间戳: startAt,
    参数: payload
  })
  try {
    const res = await axios.post('/api/dashboard/download/web', payload)
    const endAt = Date.now()
    console.log('【下载web】请求成功', {
      时间: new Date(endAt).toLocaleString('zh-CN'),
      时间戳: endAt,
      本次耗时毫秒: endAt - startAt,
      返回: res
    })

    return res
  } catch (err) {
    const endAt = Date.now()
    console.log('【下载web】请求失败', {
      时间: new Date(endAt).toLocaleString('zh-CN'),
      时间戳: endAt,
      本次耗时毫秒: endAt - startAt,
      错误: err
    })
    throw err
  }
}

// 触发短信验证码
const bySms = options => axios.post('/api/dashboard/by-sms', options)

// 短信验证
const authVerifyBySms = options => axios.post('/api/dashboard/verify/by-sms', options)

// 获取设备 ID 信息
const authInfo = options => axios.post('/api/dashboard/auth/auth-info', options)

// 绑定设备（单独 60s 超时，避免全局 500s 导致验证码后一直转圈）
const USER_BINDING_DEVICE_TIMEOUT_MS = 60 * 1000

const userBindingDevice = options =>
  axios.request({
    method: 'post',
    url: '/api/definition/user-binding-device',
    data: options,
    timeout: USER_BINDING_DEVICE_TIMEOUT_MS
  })

// 订单绑定设备

const orderBindingDevice = options => axios.post('/api/definition/order-binding-device', options)

// 获取个人中心订单

const getUserAccountDevice = options => axios.get('/api/definition/user-account-device?' + qs.stringify(options))

// 获取个人中心设备
// 短时合并相同查询，避免 Dashboard 刷新时并发打多次
const accountOrderInflight = new Map()

const getUserAccountOrder = options => {
  const query = qs.stringify(options || {})
  const key = query || '__default__'
  const existing = accountOrderInflight.get(key)
  if (existing) return existing

  const promise = axios
    .get('/api/definition/user-account-order?' + query)
    .catch(err => {
      accountOrderInflight.delete(key)
      throw err
    })
    .then(res => {
      // 保留短时缓存，同页其它调用可复用；超时后允许重新拉取
      setTimeout(() => {
        if (accountOrderInflight.get(key) === promise) {
          accountOrderInflight.delete(key)
        }
      }, 3000)

      return res
    })

  accountOrderInflight.set(key, promise)

  return promise
}

// 开启或者关闭续费
const sendWhetherToRenew = options => axios.post('/api/definition/whether-to-renew', options)

// 关闭续费并发送邮件
const sendWhetherToRenewClose = options => axios.get('/api/definition/whether-to-renew-close?' + qs.stringify(options))

// 手动续费
const sendRenew = options => axios.post('/api/renew', options)

// 通知
const getNotifications = options => axios.get('/api/definition/user-notifications')

// 通知列表

const getNotificationsList = options => axios.get('/api/definition/user-notifications-list?' + qs.stringify(options))

// 通知信息已读
const sendReadNotifications = options => axios.post('/api/definition/read-notifications', options)

// 获取所有产品价格

const getProduct = option => axios.get('/api/tsafely-products?populate=img&pagination[page]=1&pagination[pageSize]=3')

// 检查下载时间
const checkDownloadTime = options => axios.post('/api/definition/check-download-time', options)

// 修改下载时间
const sendDownloadTime = options => axios.post('/api/definition/send-download-time', options)

// 获取用户所有设备
const getAllUserDevice = (options, requestConfig = {}) =>
  axios.get('/api/definition/user-device?' + qs.stringify(options), requestConfig)

// 获取服务器当前时间
const getServerTime = () => axios.get('/api/export/get-server-time')

// 获取设备更新时间
const getDeviceResetTime = options => axios.post('/api/definition/user-device-reset-time', options)

// 获取用户优惠价
const checkCoupons = data => axios.post('/api/user/coupon', data)

// 未绑定订单
const getNotBindOrder = data => axios.post('/api/definition/not-bind-order', data)

// 是否买过（与 web 登录页同一口径）
const getUserIsBuy = options => axios.get('/api/definition/user-is-buy', options)

// 是否还有有效订单（后端按最新一笔已支付订单判断是否已过期超 30 天；无订单时返回 valid: true）
const getUserHasValidOrder = options => axios.get('/api/tsafely-order/valid', options)

// 修改密码（V2：RSA 加密后提交，失败不回退明文接口）
const updatePassword = async data => {
  const [currentPassword, newPassword] = await Promise.all([
    encryptPassword(data.currentPassword),
    encryptPassword(data.newPassword)
  ])

  return axios.post('/api/v2/definition/updatePassword', {
    currentPassword,
    newPassword
  })
}

// 修改icloud（旧接口，前端已改走 device-info-update，保留以免其它调用方报错）
const updateIcloud = data => axios.post('/api/user/update-cloud', data)

// 批量更新设备 iCloud 凭据 / 锁定状态（已有设备，不绑订单）
const updateDeviceInfo = data => axios.post('/api/v2/device/device-info-update', data)

// 获取当前设备
const userDeviceCurrent = options => axios.get('/api/definition/user-device-current')

// 校验当前绑定类型
const verifyCurrentType = option => axios.get('/api/definition/user-order-current')

// 登录
const gcloudAuth = options => axios.post('/api/gcloud/dashboard/auth', options)

// 验证
const gcloudVerify = options => axios.post('/api/gcloud/dashboard/verify', options)

// 下载（统一附带 web 产品 id）
const gcloudDownload = options =>
  axios.post('/api/gcloud/dashboard/download', {
    ...options,
    pid: getWebPid()
  })

// 机器人
const gcloudVerifyBg = options => axios.post('/api/gcloud/auth/verify/bg', options)

// 解绑设备

const currentUnBindDevice = options => axios.post('/api/device/unbind', options)

// 收集安卓错误
const addAndroidDeviceVerifyInfo = options => axios.post('/api/device-verify-info', options)
const getAndroidDeviceVerifyInfo = options => axios.get('/api/device-verify-info')

// 收集日志
const sendUserAnalytic = option => axios.post('/api/user-analytic', option)

// 添加设备布局
const sendLikeType = option => axios.post('/api/device/like-type', option)

// 谷歌云登陆
const googleSignup = option => axios.post('/api/gcloud/signin', option)

// 谷歌云登陆验证
const googleCodeSubmit = option => axios.post('/api/gcloud/signin/code/submit', option)

// 轮训谷歌云登陆状态
const googleStatusQuery = option => axios.post('/api/gcloud/signin/status/query', option)

// ios登录情况上报
const iosLoginReport = option => axios.post('/api/bind-tracking/add-one', option)

// ios设备登录验证
const iosLoginVerification = options => {
  // 本地调试：DEBUG_FORCE_ICLOUD_10005=1 时不调接口，直接当 10005
  if (isDebugForceIcloud10005()) {
    return Promise.resolve(buildDebugIcloud10005Response())
  }

  return axios.post('/api/dashboard/icloud/auth/login', options).then(response => {
    const payload = response?.data
    const message = response?.msg

    // BFF 将底层 em 提升到顶层 msg；同步到旧 UI 使用的 payload.em，兼容旧响应。
    if (
      !message ||
      !payload ||
      typeof payload !== 'object' ||
      Array.isArray(payload)
    ) {
      return response
    }

    return {
      ...response,
      data: {
        ...payload,
        msg: message,
        em: message
      }
    }
  })
}

// 下载ios数据进度状态
const downloadState = options => axios.post('/api/download/state', options)

// iOS 高级下载真实进度接口（BFF 中转）
// 返回示例：{ ec: 0, em: "Success", data: { key, value: "5641" } }；value 为万分比（10000=100%）；ec=2 Key not found 由调用方继续轮询
const iosCacheProgress = options =>
  axios.get('/api/dashboard/download-progress?' + qs.stringify(options))

// iOS 高级下载完成后删除进度缓存：BFF DELETE /api/dashboard/download-progress?key=...
const iosCacheDel = key =>
  axios.delete('/api/dashboard/download-progress?' + qs.stringify({ key }))

// 下载android数据进度状态
const gcloudDownloadState = options => axios.post('/api/gcloud/download/state', options)

export {
  getUserInfo,
  authByDevice,
  authVerifyByDevice,
  downloadWeb,
  bySms,
  authVerifyBySms,
  authInfo,
  userBindingDevice,
  orderBindingDevice,
  getUserAccountDevice,
  getUserAccountOrder,
  sendWhetherToRenew,
  sendWhetherToRenewClose,
  sendRenew,
  getNotifications,
  getNotificationsList,
  sendReadNotifications,
  getProduct,
  checkDownloadTime,
  sendDownloadTime,
  getAllUserDevice,
  getServerTime,
  getDeviceResetTime,
  checkCoupons,
  getNotBindOrder,
  getUserIsBuy,
  getUserHasValidOrder,
  updatePassword,
  updateIcloud,
  updateDeviceInfo,
  userDeviceCurrent,
  verifyCurrentType,
  gcloudAuth,
  gcloudVerify,
  gcloudDownload,
  gcloudVerifyBg,
  currentUnBindDevice,
  addAndroidDeviceVerifyInfo,
  getAndroidDeviceVerifyInfo,
  sendUserAnalytic,
  sendLikeType,
  googleSignup,
  googleCodeSubmit,
  googleStatusQuery,
  iosLoginReport,
  iosLoginVerification,
  downloadState,
  iosCacheProgress,
  iosCacheDel,
  gcloudDownloadState,
}
